import {
  Box,
  Flex,
  Heading,
  IconButton,
  Skeleton,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Text,
  Tooltip,
  Tr,
  Button,
  SimpleGrid,
  HStack,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Stack,
  Spacer,
  Alert,
  AlertIcon,
  useToast,
} from '@chakra-ui/react';
import {
  Table as TanStackTable,
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import _ from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Legend, Pie, PieChart } from 'recharts';

import {
  date as commonDate,
  finleyAssert,
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  DeliverableContainerOutputLenderViewGQLType,
  useGetAllDeliverablesThreadsByOrganizationQuery,
  useGetDeliverablesContainersLenderViewQuery,
  useGetUserQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import {
  calculateLenderContainerStatus,
  lenderContainerStatusColor,
  lenderContainerStatusIcon,
  LenderStatus,
  LenderStatusCell,
} from '../Status';
import { DeliverablesOverviewUpdatesDrawer } from '../UpdatesDrawer';
import { getDeliverableFinalOutputReceiptsViewUrl } from '../utils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { TanStackPaginationControl } from 'packages/common-frontend/components/TanStack';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { logOutOnAuthErrors } from 'packages/common-frontend/utils';
import {
  getFilterValue,
  ColumnFilter,
  customFullDateStrFilter,
  customMultiFilter,
} from 'packages/deliverables/ui/DeliverablesColumnFilter';

const computedStatusSortOrder: { [key in LenderStatus]: number } = {
  overdue: 5,
  readyForReview: 4,
  remediationRequested: 3,
  approved: 2,
  inProgress: 1,
};

const statusOptions = [
  { label: <LenderStatusCell status="overdue" />, value: 'overdue' },
  {
    label: <LenderStatusCell status="remediationRequested" />,
    value: 'remediationRequested',
  },
  {
    label: <LenderStatusCell status="readyForReview" />,
    value: 'readyForReview',
  },
  { label: <LenderStatusCell status="approved" />, value: 'approved' },
  { label: <LenderStatusCell status="inProgress" />, value: 'inProgress' },
];

const getRowBackgroundColor = (
  container: DeliverableContainerOutputLenderViewGQLType,
) => {
  const status = calculateLenderContainerStatus(container);

  switch (status) {
    case 'overdue':
      return 'red.100';
    default:
      return 'none';
  }
};

const filterStatuses = (
  table: TanStackTable<DeliverableContainerOutputLenderViewGQLType>,
  status: string,
) => {
  const column = table.getColumn('calculatedStatus');
  if (column) {
    const currentFilters = getFilterValue(column);
    const index = currentFilters.indexOf(status);
    if (index >= 0) {
      currentFilters.splice(index, 1);
    } else {
      currentFilters.push(status);
    }
    column.setFilterValue(currentFilters);
  }
};

type StatusFilterCardsProps = {
  includesStatus: boolean;
  title: string;
  status: LenderStatus;
  onClick: () => void;
};
const StatusFilterCards = ({
  includesStatus,
  title,
  status,
  onClick,
}: StatusFilterCardsProps) => {
  return (
    <Button
      leftIcon={
        <Icon
          icon={lenderContainerStatusIcon[status]}
          color={`${lenderContainerStatusColor[status]}.300`}
        />
      }
      justifyContent="flex-start"
      py={6}
      variant="outline"
      borderColor={includesStatus ? 'black' : 'gray.100'}
      onClick={onClick}
      _focus={{ boxShadow: 'none' }}
    >
      {title}
    </Button>
  );
};

type DeliverablesReceiptsLenderTableProps = {
  containers: DeliverableContainerOutputLenderViewGQLType[];
};
const DeliverablesReceiptsLenderTable = ({
  containers,
}: DeliverablesReceiptsLenderTableProps) => {
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'calculatedStatus', desc: true },
    { id: 'dueDate', desc: false },
  ]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([
    {
      id: 'calculatedStatus',
      value: ['overdue', 'remediationRequested', 'readyForReview'],
    },
  ]);
  const navigate = useNavigate();

  const statusCounts = useMemo(() => {
    let overdueCount: number = 0;
    let readyForReviewCount: number = 0;
    let remediationRequiredCount: number = 0;
    let approvedCount: number = 0;
    let inProgressCount: number = 0;
    for (const container of containers) {
      const status = calculateLenderContainerStatus(container);
      switch (status) {
        case 'approved':
          approvedCount += 1;
          break;
        case 'inProgress':
          inProgressCount += 1;
          break;
        case 'overdue':
          overdueCount += 1;
          break;
        case 'remediationRequested':
          remediationRequiredCount += 1;
          break;
        case 'readyForReview':
          readyForReviewCount += 1;
          break;
        default:
          finleyAssertUnreachable(status);
          break;
      }
    }

    return {
      overdueCount,
      readyForReviewCount,
      remediationRequiredCount,
      approvedCount,
      inProgressCount,
    };
  }, [containers]);

  const statusFilters = useMemo(() => {
    return _.flatMap(
      columnFilters
        .filter((cf) => cf.id === 'calculatedStatus')
        .map((cf) => cf.value),
    );
  }, [columnFilters]);

  const columns: ColumnDef<
    DeliverableContainerOutputLenderViewGQLType,
    any
  >[] = [
    {
      accessorKey: 'creditFacilitySlug',
      header: 'Credit Facility',
      enableSorting: false,
      cell: ({ getValue }) => commonUtils.toTitleCase(getValue()),
      filterFn: customMultiFilter,
      meta: {
        filterHeader: 'Select Credit Facility/Facilities',
      },
    },
    {
      accessorKey: 'configName',
      header: 'Name',
      enableSorting: false,
      cell: ({ getValue }) => commonUtils.toTitleCase(getValue()),
    },
    {
      id: 'reportType',
      accessorKey: 'type',
      enableSorting: false,
      header: 'Report Type',
      cell: ({ getValue }) => commonUtils.toTitleCase(getValue()),
      filterFn: customMultiFilter,
      meta: {
        filterHeader: 'Select Report Type(s)',
      },
    },
    {
      id: 'dueDate',
      accessorKey: 'dueDate',
      enableSorting: true,
      header: 'Due Date',
      cell: ({ getValue }) =>
        commonDate.fullDateStrToHumanDateString(getValue()),
      filterFn: customFullDateStrFilter,
      meta: {
        filterHeader: 'Select Due Date',
        filterType: 'date',
      },
    },
    {
      id: 'approvedDate',
      enableSorting: true,
      header: 'Approved Date',
      cell: ({ row }) => {
        const { approvedDate } = row.original;

        return approvedDate
          ? commonDate.fullDateStrToHumanDateString(
              commonDate.epochToFullDateStr(approvedDate),
            )
          : '-';
      },
      filterFn: customFullDateStrFilter,
      meta: {
        filterHeader: 'Select Approved Date',
        filterType: 'date',
      },
    },
    {
      id: 'calculatedStatus',
      accessorFn: (row) => calculateLenderContainerStatus(row),
      enableColumnFilter: true,
      header: 'Status',
      // eslint-disable-next-line react/display-name
      cell: ({ row }) => (
        <LenderStatusCell
          status={calculateLenderContainerStatus(row.original)}
        />
      ),
      sortingFn: (rowA, rowB) => {
        const statusRowA = calculateLenderContainerStatus(rowA.original);
        const statusRowB = calculateLenderContainerStatus(rowB.original);
        if (
          computedStatusSortOrder[statusRowA] >
          computedStatusSortOrder[statusRowB]
        ) {
          return 1;
        }
        if (
          computedStatusSortOrder[statusRowA] <
          computedStatusSortOrder[statusRowB]
        ) {
          return -1;
        }
        return 0;
      },
      filterFn: customMultiFilter,
      meta: {
        filterHeader: 'Select Status(es)',
        filterOptions: statusOptions,
      },
    },
    {
      id: 'moreDetail',
      enableSorting: false,
      enableColumnFilter: false,
      // eslint-disable-next-line react/display-name
      cell: ({ row }) => {
        const { id, latestReceiptId } = row.original;
        if (!latestReceiptId) {
          return <></>;
        }
        return (
          <Tooltip bg="gray.500" hasArrow label="More details">
            <IconButton
              aria-label="more details"
              icon={<Icon icon="chevron-right" />}
              variant="ghost"
              onClick={() =>
                navigate(
                  getDeliverableFinalOutputReceiptsViewUrl({
                    containerId: id,
                    receiptId: latestReceiptId,
                  }),
                )
              }
            />
          </Tooltip>
        );
      },
      meta: {
        style: {
          padding: '0px',
          textAlign: 'right',
        },
      },
    },
  ];

  const table = useReactTable({
    data: containers,
    columns,
    enableMultiSort: true,
    state: {
      sorting,
      columnFilters,
    },
    initialState: {
      sorting,
      columnFilters,
      pagination: {
        pageIndex: 0,
        pageSize: 35,
      },
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  return (
    <Box>
      <HStack width="full" spacing="20px">
        {/* Pie Chart! */}
        <PieChart width={500} height={360}>
          <Pie
            width={160}
            height={160}
            data={[
              {
                name: `Overdue`,
                value: statusCounts.overdueCount,
                fill: colors.red[200],
              },
              {
                name: `Remediation Requested`,
                value: statusCounts.remediationRequiredCount,
                fill: colors.orange[400],
              },
              {
                name: `Ready For Review`,
                value: statusCounts.readyForReviewCount,
                fill: colors.orange[300],
              },
              {
                name: `In Progress`,
                value: statusCounts.inProgressCount,
                fill: colors.orange[200],
              },
              {
                name: `Approved`,
                value: statusCounts.approvedCount,
                fill: colors.green[200],
              },
            ]}
            dataKey="value"
            nameKey="name"
          />
          <Legend
            verticalAlign="middle"
            align="right"
            layout="vertical"
            iconType="circle"
            iconSize={8}
            formatter={(value) => (
              <Text color={colors.black[70]} display="inline-block" ml="8px">
                {value}
              </Text>
            )}
          />
        </PieChart>

        {/* Hot Buttons / Filter Cards */}
        <SimpleGrid width="full" columns={1} spacing={4} mb={8}>
          <StatusFilterCards
            includesStatus={statusFilters.includes('overdue')}
            title={`Overdue ${statusCounts.overdueCount}`}
            status="overdue"
            onClick={() => {
              filterStatuses(table, 'overdue');
            }}
          />
          <StatusFilterCards
            includesStatus={statusFilters.includes('remediationRequested')}
            title={`Remediation Requested ${statusCounts.remediationRequiredCount}`}
            status="remediationRequested"
            onClick={() => {
              filterStatuses(table, 'remediationRequested');
            }}
          />
          <StatusFilterCards
            includesStatus={statusFilters.includes('readyForReview')}
            title={`Ready For Review ${statusCounts.readyForReviewCount}`}
            status="readyForReview"
            onClick={() => {
              filterStatuses(table, 'readyForReview');
            }}
          />
          <StatusFilterCards
            includesStatus={statusFilters.includes('approved')}
            title={`Approved ${statusCounts.approvedCount}`}
            status="approved"
            onClick={() => {
              filterStatuses(table, 'approved');
            }}
          />
          <StatusFilterCards
            includesStatus={statusFilters.includes('inProgress')}
            title={`In Progress ${statusCounts.inProgressCount}`}
            status="inProgress"
            onClick={() => {
              filterStatuses(table, 'inProgress');
            }}
          />
        </SimpleGrid>
      </HStack>
      <Table borderWidth="1px" borderColor={colors.gray[100]}>
        <Thead>
          {_.map(table.getHeaderGroups(), (headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const columnCanSort = header.column.getCanSort();
                const columnCanFilter = header.column.getCanFilter();

                return (
                  <Th
                    key={header.id}
                    colSpan={header.colSpan}
                    textTransform="capitalize"
                    padding={
                      header.column.columnDef.meta?.style?.padding ||
                      '8px 16px 8px 16px'
                    }
                    fontSize="16px"
                    fontWeight="medium"
                    whiteSpace="nowrap"
                    color={colors.black[95]}
                    width="auto"
                  >
                    <Flex alignItems="center">
                      <Box
                        cursor={columnCanSort ? 'pointer' : 'default'}
                        onClick={header.column.getToggleSortingHandler()}
                        mr="8px"
                        color={colors.aurora[400]}
                        fontWeight="thin"
                      >
                        {{
                          asc: <Icon icon="sort-up" />,
                          desc: <Icon icon="sort-down" />,
                        }[header.column.getIsSorted() as string] ??
                          (columnCanSort ? <Icon icon="sort" /> : null)}
                      </Box>
                      <Box>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Box>
                      {columnCanFilter && (
                        <ColumnFilter
                          key={JSON.stringify(getFilterValue(header.column))}
                          type={header.column.columnDef.meta?.filterType}
                          header={header.column.columnDef.meta?.filterHeader}
                          options={header.column.columnDef.meta?.filterOptions}
                          column={header.column}
                          valueFormatFn={
                            header.column.columnDef.meta?.filterFormatFn
                          }
                        />
                      )}
                    </Flex>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody color="gray.600" fontSize="sm">
          {table.getRowModel().rows.map((row) => (
            <Tr
              key={row.id}
              backgroundColor={getRowBackgroundColor(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <Td
                  key={cell.id}
                  textAlign={cell.column.columnDef.meta?.style?.textAlign}
                  padding={cell.column.columnDef.meta?.style?.padding}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
      <TanStackPaginationControl table={table} />
    </Box>
  );
};

const LenderTaskCenterButton = ({
  container,
  status,
}: {
  container: DeliverableContainerOutputLenderViewGQLType;
  status: LenderStatus;
}) => {
  const navigate = useNavigate();

  const onClick = useCallback(() => {
    switch (status) {
      case 'overdue': {
        if (container.latestReceiptId) {
          navigate(
            getDeliverableFinalOutputReceiptsViewUrl({
              containerId: container.id,
              receiptId: container.latestReceiptId,
            }),
          );
        }
        break;
      }
      case 'remediationRequested':
      case 'readyForReview':
        finleyAssert(
          container.latestReceiptId,
          `Cannot have status: ${status} without a latestReceiptId`,
        );
        navigate(
          getDeliverableFinalOutputReceiptsViewUrl({
            containerId: container.id,
            receiptId: container.latestReceiptId,
          }),
        );
        break;
      default:
        break;
    }
  }, [container.id, container.latestReceiptId, navigate, status]);
  return (
    <Button
      wordBreak="break-word"
      whiteSpace="pre-line"
      textAlign="left"
      px={2}
      py={8}
      fontWeight="normal"
      fontSize="sm"
      rightIcon={
        container.latestReceiptId ? (
          <Icon icon="chevron-right" color="blue.300" />
        ) : (
          <></>
        )
      }
      onClick={onClick}
      isDisabled={!container.latestReceiptId}
      _disabled={{ opacity: 1, cursor: 'inherit' }}
    >
      {`${commonUtils.toTitleCase(
        container.creditFacilitySlug,
      )} \n ${commonUtils.toTitleCase(
        container.configKey,
      )} - ${commonDate.fullDateStrToShortMonthDayString(container.dueDate)}`}
    </Button>
  );
};

const LenderTaskCenterByStatus = ({
  containersWithStatus,
}: {
  containersWithStatus: DeliverableContainerOutputLenderViewGQLType[];
}) => {
  if (containersWithStatus.length === 0) {
    return <></>;
  }

  const status = calculateLenderContainerStatus(containersWithStatus[0]);
  return (
    <>
      <LenderStatusCell status={status} fontWeight="semibold" />
      {containersWithStatus.map((container) => {
        return (
          <LenderTaskCenterButton
            key={`${container.id}-${status}`}
            container={container}
            status={status}
          />
        );
      })}
    </>
  );
};

type NextStepsContainers = {
  overdue: DeliverableContainerOutputLenderViewGQLType[];
  remediationRequested: DeliverableContainerOutputLenderViewGQLType[];
  readyForReview: DeliverableContainerOutputLenderViewGQLType[];
  size: number;
};

const getNextStepsContainersLender = (
  containers: DeliverableContainerOutputLenderViewGQLType[],
): NextStepsContainers => {
  const overdue = [];
  const remediationRequested = [];
  const readyForReview = [];
  let size = 0;

  for (const container of containers) {
    const status = calculateLenderContainerStatus(container);

    switch (status) {
      case 'readyForReview':
        readyForReview.push(container);
        size += 1;
        break;
      case 'overdue':
        overdue.push(container);
        size += 1;
        break;
      case 'remediationRequested':
        remediationRequested.push(container);
        size += 1;
        break;
      default:
        break;
    }
  }

  return {
    readyForReview,
    overdue,
    remediationRequested,
    size,
  };
};

const LenderTaskCenterDrawer = ({
  isOpen,
  onClose,
  containers,
}: {
  isOpen: boolean;
  onClose: () => void;
  containers: NextStepsContainers;
}) => {
  const { overdue, remediationRequested, readyForReview } = containers;
  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      closeOnOverlayClick
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton _focus={{ boxShadow: 'none' }} />
        <DrawerHeader>Tasks</DrawerHeader>

        <DrawerBody>
          <Stack spacing="20px">
            <LenderTaskCenterByStatus containersWithStatus={overdue} />
            <LenderTaskCenterByStatus
              containersWithStatus={remediationRequested}
            />
            <LenderTaskCenterByStatus containersWithStatus={readyForReview} />
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

// lender page containing a table of all the deliverable they can view
type DeliverablesContainersLenderViewProps = {
  creditFacilityIds: string[];
};
export const DeliverablesContainersLenderView = ({
  creditFacilityIds,
}: DeliverablesContainersLenderViewProps) => {
  const toast = useToast();
  const navigate = useNavigate();

  const {
    data: lenderDeliverablesContainersData,
  } = useGetDeliverablesContainersLenderViewQuery({
    fetchPolicy: 'network-only',
    variables: {
      creditFacilityIds,
    },
    onError: ({ graphQLErrors }) => {
      logOutOnAuthErrors(graphQLErrors, navigate);
    },
  });

  const { data: userData } = useGetUserQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  const {
    data: threadsData,
    refetch: refetchThreadsData,
  } = useGetAllDeliverablesThreadsByOrganizationQuery({
    fetchPolicy: 'no-cache',
    variables: {
      organizationId: userData?.user?.organizationId!,
    },
    skip: !userData?.user,
    onError: (err) => {
      errorToastConfig.title = commonUtils.EnvUtils.isProduction()
        ? 'An unexpected error occurred. Please contact Finley for support.'
        : err.message;
      toast(errorToastConfig);
    },
  });

  // drawer buttons & mechanics
  const [isUpdatesDrawerOpen, setIsUpdatesDrawerOpen] = useState<boolean>(
    false,
  );
  const [isTaskDrawerOpen, setIsTaskDrawerOpen] = useState<boolean>(false);

  const TopLevelIconButtons = (
    <>
      {/* SARAH TODO: LENDER insights & updates are not functional rn */}
      <Tooltip label="Deliverables Insights">
        <IconButton
          aria-label="Deliverables Insights"
          key="Deliverables Insights"
          icon={<Icon icon="chart-pie-simple" />}
          _focus={{ boxShadow: 'none' }}
        />
      </Tooltip>
      <Tooltip label="Updates">
        <IconButton
          aria-label="Updates"
          key="Updates"
          icon={<Icon icon="message-lines" />}
          _focus={{ boxShadow: 'none' }}
          onClick={() => {
            setIsUpdatesDrawerOpen(true);
          }}
        />
      </Tooltip>
      <Tooltip label="Tasks">
        <IconButton
          aria-label="Tasks"
          key="Tasks"
          icon={<Icon icon="list-check" />}
          _focus={{ boxShadow: 'none' }}
          onClick={() => {
            setIsTaskDrawerOpen(true);
          }}
        />
      </Tooltip>
    </>
  );

  if (
    !threadsData ||
    !lenderDeliverablesContainersData ||
    !lenderDeliverablesContainersData.deliverablesContainersLenderView ||
    !userData ||
    !userData.user
  ) {
    return (
      <Skeleton
        data-testid="loading-deliverables-v4-lender-view"
        height="600px"
      />
    );
  }

  const lenderDeliverables: DeliverableContainerOutputLenderViewGQLType[] =
    lenderDeliverablesContainersData.deliverablesContainersLenderView;

  const nextStepsDeliverableContainers = getNextStepsContainersLender(
    lenderDeliverables,
  );

  const { id: userId, organizationId } = userData.user;

  return (
    <Box height="100%">
      {nextStepsDeliverableContainers.size > 0 && (
        <Alert status="info" mb={4} borderRadius="8px">
          <AlertIcon />
          <HStack fontSize="sm" width="full">
            <Text>You have pending tasks that need your attention.</Text>
            <Spacer />
            <Button
              variant="link"
              color="blue.400"
              fontSize="sm"
              onClick={() => {
                setIsTaskDrawerOpen(true);
              }}
              _focus={{ boxShadow: 'none' }}
            >
              Next Steps
            </Button>
          </HStack>
        </Alert>
      )}
      <Flex justify="space-between">
        <HStack width="full" pb="16px">
          <Heading as="h2">Deliverables</Heading>
          <Spacer />
          {TopLevelIconButtons}
        </HStack>
      </Flex>
      {lenderDeliverables.length === 0 ? (
        <Text>No deliverables are currently configured.</Text>
      ) : (
        <DeliverablesReceiptsLenderTable containers={lenderDeliverables} />
      )}
      <DeliverablesOverviewUpdatesDrawer
        type="lender"
        isOpen={isUpdatesDrawerOpen}
        onClose={() => {
          setIsUpdatesDrawerOpen(false);
        }}
        organizationId={organizationId}
        userId={userId}
        threadsWithDeliverablesMetadata={
          threadsData.deliverablesThreadsByOrganization
        }
        refetchThreads={refetchThreadsData}
      />
      <LenderTaskCenterDrawer
        isOpen={isTaskDrawerOpen}
        onClose={() => {
          setIsTaskDrawerOpen(false);
        }}
        containers={nextStepsDeliverableContainers}
      />
    </Box>
  );
};
